import useSWR from "swr";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { fetcher } from "../../util/axios";
import PageSpinner from "../../components/PageSpinner";
import placeholder from "../../util/placeholder";
import { useCurrency } from "../../hooks/useCurrency";
import { Button } from "../../components/ui/button";
import { ChevronLeft, Loader } from "lucide-react";
import dayjs from "dayjs";
import useSWRMutation from "swr/mutation";
import { message } from "antd";
import { handleMutation } from "../../util/axios";
import requestErrorHandler from '../../util/requestErrorHandler';

export default function VehicleHistoryPage() {
  const { requestId, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const vehicle = location?.state?.vehicle;
  const [formatCurrency] = useCurrency();

  const { data, isLoading, mutate } = useSWR(requestId ? { url: `fleet/${requestId}/${id}/historyDetails` } : null, fetcher);
  const history: any = {
    ...data?.data,
    ...data?.data?.vehicleHistories[0],
  };

  const { trigger: triggerArchiveTransaction, isMutating: isArchiving } = useSWRMutation(
    `/fleet/${requestId}/archive-transaction`,
    handleMutation
  );

  const archiveTransaction = async () => {
    try {
      const res = await triggerArchiveTransaction();
      mutate();
      if (res.success) {
        message.success(res.message);
      }
    } catch (error) {
      requestErrorHandler(error);
    }
  }  

  if (isLoading) return <PageSpinner />;

  return (
    <div className="h-full flex flex-col overflow-hidden border rounded-lg">
      <div className="p-2 border-b flex items-center justify-between">
        <div className="flex items-center gap-3">
            <Button variant="ghost" className="text-gray-500 hover:text-blue-600 px-2 py-1" onClick={() => navigate(-1)}>
              <ChevronLeft className="!size-6" />
            </Button>
            <h2 className="text-sm font-semibold">Vehicle Overview - {" "}
              <span className="text-blue-600 font-semibold">{vehicle?.regPlate}</span> - {" "}
              <span className="text-blue-600 font-semibold">{vehicle?.referenceId}</span>
            </h2>
        </div>
      </div>
      <div className="flex-1 flex flex-col overflow-auto bg-white rounded p-4">
        <div className="flex items-center gap-4 mb-4">
            <div className="size-16 border rounded flex items-center justify-center bg-gray-50">
              <img
                src={vehicle?.truckCategoryDetails?.image || placeholder}
                alt="truck"
                className="object-contain size-10"
              />
            </div>
            <div className="self-end">
                <p className="text-lg font-semibold capitalize">
                  {history?.vehicleTrips?.materialName}
                </p>
            </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-6">
            <div className="mt-8 space-y-3">
                <p className="flex items-center gap-1 text-xs text-gray-500 font-medium">
                  Date of operation:
                  <span className="text-sm font-medium">{dayjs(history?.operationDate).format("DD/MM/YYYY @ hh:mm A")}</span>
                </p>
                <p className="flex items-center gap-1 text-xs text-gray-500 font-medium">
                  Trips requested:
                  <span className="text-sm font-medium">{history?.numberOfTripsRequested}</span>
                </p>

                <p className="flex items-center gap-1 text-xs text-gray-500 font-medium">
                  Rental value:
                  <span className="text-orange-500 font-semibold">{formatCurrency(history.rentalValue)}</span>
                </p>
                
                <p className="flex items-center gap-1 text-xs text-gray-500 font-medium">
                  Assigned By:
                  <span className="text-sm text-blue-600 font-medium">{history?.assignedBy}</span>
                  <span className="text-gray-400 text-xs font-medium">{dayjs(history?.assignmentDateTime).format("DD/MM/YYYY @ hh:mm A")}</span>
                </p>

                <p className="flex items-center gap-1 text-xs text-gray-500 font-medium">
                  Accepted:
                  <span className="text-sm font-medium">
                    {history?.fleetOwnerAcceptanceDateTime && dayjs(history?.fleetOwnerAcceptanceDateTime).format("DD/MM/YYYY @ hh:mm A")}
                  </span>
                </p>

                <p className="flex items-center gap-1 text-xs text-gray-500 font-medium">
                  Completed:
                  <span className="text-sm font-medium">
                    {history?.completedDate && dayjs(history?.completedDate).format("DD/MM/YYYY @ hh:mm A")}
                  </span>
                </p>
            </div>

            <div className="mt-8">
              <div className="text-sm font-medium mb-2">Loading Point</div>
              <div className="p-3 bg-sky-50 rounded border-l-4 border-sky-200">
                <div className="font-semibold">{history?.pickupZone?.title}</div>
                <div className="mt-2 p-2 bg-white rounded border text-sm text-gray-500">{history?.vehicleTrips?.loadingPoint}</div>
              </div>

              <div className="mt-6 text-sm font-medium mb-2">Dumping Point</div>
              <div className="p-3 bg-sky-50 rounded border-l-4 border-sky-200">
                <div className="font-semibold">{history?.dropoffZone?.title}</div>
                <div className="mt-2 p-2 bg-white rounded border text-sm text-gray-500">{history?.vehicleTrips?.dumpingPoint}</div>
              </div>
            </div>
          </div>

          <div className="md:col-span-4">
            {/* Right column repeated trip completion & CTA already handled above in md:col-span-4 in the two-column area */}
              <div>
                <div className="mb-4 p-2 bg-sky-50 rounded">
                  <div className="bg-white flex items-center gap-4 p-2 rounded">
                    <div className="size-10 border border-primary rounded flex items-center justify-center bg-gray-50">
                      <img
                        src={vehicle?.truckCategoryDetails?.image || placeholder}
                        alt="truck"
                        className="object-contain size-8"
                      />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">
                        {vehicle?.regPlate}
                      </div>
                      <div className="text-xs font-medium text-gray-500">Last Trip <span className="text-xs text-primary">
                        {history?.vehicleTrips?.tripCompletionDate && dayjs(history?.vehicleTrips?.tripCompletionDate).format("MMM DD YYYY @ hh:mm A")}
                        </span></div>
                    </div>
                    <div className="text-blue-600 font-semibold ml-auto">{history?.vehicleTrips?.tripCount}</div>
                  </div>

                  <div className="mt-3 text-sm text-gray-600">
                    {history?.completedTrips?.map((trip: any) => (
                      <div key={trip.id} className="flex justify-between">
                        <p>Trip {trip.tripNumber}</p>
                        <p className="text-blue-600 font-medium">{dayjs(trip.completionAt).format("DD/MM/YYYY @ hh:mm")}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Archive Transaction Button */}
                {history?.vehicleTrips?.status === 'COMPLETED' && (
                  <Button
                    className="w-full bg-blue-600 text-white mt-8"
                    disabled={isArchiving || history?.vehicleTrips?.status === 'ARCHIVED'}
                    onClick={archiveTransaction}
                  >
                    {isArchiving ? <Loader /> : 'Archive Transaction'}
                  </Button>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
